/**
 * FinanceDocumentModal — Create + Edit reuse for finance_documents.
 *
 * mode="create": all fields editable (incl. type segmented control),
 * optional file input visible, Submit calls createAdminFinanceDocument.
 *
 * mode="edit": type toggle disabled (immutable after create per backend
 * spec §6.2.4 PUT), file input hidden (file-replace UI is Phase 1c),
 * Submit calls updateAdminFinanceDocument.
 *
 * Loaders (eager on open, single-shot per Modal-open):
 *   units, tenants, tenancies, orgUsers
 * Lazy loader on [type, tenancyId] for tenancyRevenueOptions when
 * type=rent_payment + tenancyId is set.
 *
 * Naming + categories must match backend CATEGORIES_BY_TYPE in
 * app/services/finance_document_service.py — drift surfaces as
 * HTTP 422 with German detail message.
 *
 * See docs/spec-finance-frontend.md §6 for the full spec.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  createAdminFinanceDocument,
  deleteAdminFinanceDocument,
  fetchAdminTenanciesAll,
  fetchAdminTenancyRevenue,
  fetchAdminTenants,
  fetchAdminUnits,
  fetchAdminUsers,
  updateAdminFinanceDocument,
} from "../../api/adminData";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";



// ─── Constants — mirror backend CATEGORIES_BY_TYPE ──────────────────


const CATEGORIES_BY_TYPE = {
  expense: [
    { value: "miete_vermieter", label: "Miete Vermieter" },
    { value: "moebel_einrichtung", label: "Möbel & Einrichtung" },
    { value: "reinigung", label: "Reinigung" },
    { value: "reparatur_wartung", label: "Reparatur & Wartung" },
    { value: "verbrauchsmaterial", label: "Verbrauchsmaterial" },
    { value: "internet_software", label: "Internet & Software" },
    { value: "marketing", label: "Marketing" },
    { value: "versicherung", label: "Versicherung" },
    { value: "buchhaltung_beratung", label: "Buchhaltung & Beratung" },
    { value: "gebuehren_banken", label: "Gebühren & Banken" },
    { value: "sonstiges", label: "Sonstiges" },
  ],
  income: [
    { value: "kaution", label: "Kaution" },
    { value: "reinigungspauschale", label: "Reinigungspauschale" },
    { value: "schadenersatz", label: "Schadenersatz" },
    { value: "rueckerstattung", label: "Rückerstattung" },
    { value: "sonstiges", label: "Sonstiges" },
  ],
  rent_payment: [
    { value: "mieteingang", label: "Mieteingang" },
  ],
};

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const ACCEPTED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  ".heic",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
].join(",");


// ─── Helpers ────────────────────────────────────────────────────────


function isoToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function toIsoDate(value) {
  if (!value) return "";
  // Accept full ISO timestamp from API or plain YYYY-MM-DD
  if (typeof value === "string" && value.length >= 10) return value.slice(0, 10);
  try {
    const d = new Date(value);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

const labelClass =
  "mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-[#4a5070]";
const inputClass =
  "block w-full rounded-[8px] border border-[#1e2130] bg-[#0d0f14] px-3 py-2 text-[13px] text-[#c5cbe0] outline-none focus:border-[#3a3f55]";


// ─── Component ──────────────────────────────────────────────────────


export default function FinanceDocumentModal({
  open,
  mode,
  initialValues,
  onClose,
  onSubmitted,
}) {
  // Form state
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("reinigung");
  const [docDate, setDocDate] = useState(() => isoToday());
  const [description, setDescription] = useState("");
  const [unitId, setUnitId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [tenancyId, setTenancyId] = useState("");
  const [tenancyRevenueId, setTenancyRevenueId] = useState("");
  const [paidByUserId, setPaidByUserId] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // SP6: Soft-Delete confirmation + in-flight state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Dropdown options (eager on open)
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);

  // TenancyRevenue options (lazy on tenancyId + type=rent_payment)
  const [tenancyRevenueOptions, setTenancyRevenueOptions] = useState([]);

  // ─── (1) State-Reset bei mode="create" + open=true ───────────────
  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      setType("expense");
      setAmount("");
      setCategory(CATEGORIES_BY_TYPE.expense[0].value);
      setDocDate(isoToday());
      setDescription("");
      setUnitId("");
      setTenantId("");
      setTenancyId("");
      setTenancyRevenueId("");
      setPaidByUserId("");
      setFile(null);
      setSubmitting(false);
    }
  }, [open, mode]);

  // ─── (2) State-Pre-Fill bei mode="edit" + initialValues ──────────
  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initialValues) {
      setType(initialValues.type || "expense");
      setAmount(
        initialValues.amount_chf != null ? String(initialValues.amount_chf) : ""
      );
      setCategory(initialValues.category || "");
      setDocDate(toIsoDate(initialValues.document_date));
      setDescription(initialValues.description || "");
      setUnitId(initialValues.unit_id || "");
      setTenantId(initialValues.tenant_id || "");
      setTenancyId(initialValues.tenancy_id || "");
      setTenancyRevenueId(initialValues.tenancy_revenue_id || "");
      setPaidByUserId(initialValues.paid_by_user_id || "");
      setFile(null); // file-Input ist im edit-Mode versteckt
      setSubmitting(false);
    }
  }, [open, mode, initialValues]);

  // ─── (3) Eager-Load der 4 Dropdown-Datenquellen (Spec §4.2.3) ────
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    void Promise.all([
      fetchAdminUnits().catch(() => []),
      fetchAdminTenants({ limit: 200 }).catch(() => []),
      fetchAdminTenanciesAll().catch(() => []),
      fetchAdminUsers().catch(() => []),
    ]).then(([u, t, ten, usr]) => {
      if (cancelled) return;
      setUnits(Array.isArray(u?.items) ? u.items : Array.isArray(u) ? u : []);
      setTenants(Array.isArray(t?.items) ? t.items : Array.isArray(t) ? t : []);
      setTenancies(
        Array.isArray(ten?.items) ? ten.items : Array.isArray(ten) ? ten : []
      );
      setOrgUsers(
        Array.isArray(usr?.items) ? usr.items : Array.isArray(usr) ? usr : []
      );
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // ─── (4) Lazy-Load tenancyRevenueOptions (Spec §6.8) ─────────────
  useEffect(() => {
    if (!open) return undefined;
    if (type !== "rent_payment" || !tenancyId) {
      setTenancyRevenueOptions([]);
      return undefined;
    }
    let cancelled = false;
    void fetchAdminTenancyRevenue(tenancyId)
      .then((rows) => {
        if (cancelled) return;
        // Nur type='rent' Zeilen sind valid für rent_payment-FK (Backend §5.1.3)
        const rentOnly = Array.isArray(rows)
          ? rows.filter((r) => r.type === "rent")
          : [];
        setTenancyRevenueOptions(rentOnly);
      })
      .catch(() => {
        if (cancelled) return;
        setTenancyRevenueOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, type, tenancyId]);

  // ─── Type → Category-Sync (Spec §6.5) ────────────────────────────
  function handleTypeChange(nextType) {
    if (mode === "edit") return; // Type ist nach Create immutable
    setType(nextType);
    const firstCat = CATEGORIES_BY_TYPE[nextType][0]?.value || "";
    setCategory(firstCat);
    if (nextType !== "rent_payment") {
      setTenancyRevenueId("");
    }
  }

  function handleFileChange(e) {
    const picked = e.target.files?.[0] || null;
    if (!picked) {
      setFile(null);
      return;
    }
    if (picked.size > MAX_FILE_SIZE_BYTES) {
      toast.error(
        `Datei zu groß (max ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB).`
      );
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(picked);
  }

  const categoryOptions = useMemo(() => {
    return CATEGORIES_BY_TYPE[type] || [];
  }, [type]);

  // ─── Submit-Flow (Spec §6.10) ────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();

    // Client-side Pflicht-Felder
    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      toast.error("Betrag muss eine positive Zahl sein.");
      return;
    }
    if (!category) {
      toast.error("Kategorie ist erforderlich.");
      return;
    }
    if (!docDate) {
      toast.error("Datum ist erforderlich.");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "create") {
        await createAdminFinanceDocument({
          type,
          amount_chf: amountNum,
          category,
          document_date: docDate,
          description: description || undefined,
          unit_id: unitId || undefined,
          tenant_id: tenantId || undefined,
          tenancy_id: tenancyId || undefined,
          tenancy_revenue_id: tenancyRevenueId || undefined,
          paid_by_user_id: paidByUserId || undefined,
          file: file || undefined,
        });
        toast.success("Beleg erfasst.");
      } else {
        // mode === "edit"
        await updateAdminFinanceDocument(initialValues.id, {
          amount_chf: amountNum,
          category,
          document_date: docDate,
          description: description || null,
          unit_id: unitId || null,
          tenant_id: tenantId || null,
          tenancy_id: tenancyId || null,
          tenancy_revenue_id: tenancyRevenueId || null,
          paid_by_user_id: paidByUserId || null,
        });
        toast.success("Beleg gespeichert.");
      }
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpenChange(next) {
    if (!next && !submitting && !deleting) {
      onClose?.();
    }
  }

  /**
   * Soft-delete handler (spec §7.3). Sets deleted_at server-side;
   * R2 file is retained for the Schweizer OR Art. 958f 10-year
   * retention period (backend service spec §5.5).
   */
  async function handleDelete() {
    if (!initialValues?.id) return;
    setDeleting(true);
    try {
      await deleteAdminFinanceDocument(initialValues.id);
      toast.success("Beleg in Papierkorb verschoben.");
      setDeleteConfirmOpen(false);
      onSubmitted?.(); // parent triggers close + load
    } catch (err) {
      toast.error(err?.message || "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  }

  const title = mode === "edit" ? "Beleg bearbeiten" : "Beleg erfassen";
  const submitLabel = mode === "edit" ? "Speichern" : "Beleg erfassen";

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Beleg-Felder anpassen. Datei-Ersetzen kommt in einem späteren Update."
              : "Quittung, Eingangsrechnung oder Mietzahlung erfassen. Datei optional."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-3">
          {/* §6.4a Type-Toggle (Segmented Control) */}
          <div>
            <label className={labelClass}>Typ</label>
            <div
              className="flex gap-2"
              role="group"
              aria-label="Beleg-Typ"
            >
              {["expense", "income", "rent_payment"].map((t) => {
                const isActive = type === t;
                const isDisabled = mode === "edit";
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => handleTypeChange(t)}
                    className={[
                      "flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors",
                      isActive
                        ? "bg-[#5b8cff] border-[#5b8cff] text-white"
                        : "bg-[#0d0f14] border-[#1e2130] text-[#94a3b8] hover:bg-[#13161f]",
                      isDisabled ? "opacity-60 cursor-not-allowed" : "",
                    ].join(" ")}
                  >
                    {t === "expense"
                      ? "Ausgabe"
                      : t === "income"
                      ? "Einnahme"
                      : "Mieteingang"}
                  </button>
                );
              })}
            </div>
            {mode === "edit" ? (
              <p className="mt-1 text-[10px] text-[#4a5070]">
                Typ ist nach dem Erfassen nicht mehr änderbar.
              </p>
            ) : null}
          </div>

          {/* §6.9 File-Input (mode="create" only — file-replace ist Phase 1c) */}
          {mode === "create" ? (
            <div>
              <label className={labelClass}>Beleg-Datei (optional)</label>
              <input
                type="file"
                accept={ACCEPTED_MIME}
                capture="environment"
                onChange={handleFileChange}
                className="block w-full text-[12px] text-[#c5cbe0] file:mr-3 file:rounded-md file:border-0 file:bg-[#1e2238] file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-[#5b8cff] hover:file:bg-[#2a304b]"
              />
              {file ? (
                <p className="mt-1 text-[11px] text-[#4a5070]">
                  {file.name} · {formatBytes(file.size)}
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-[#4a5070]">
                  Kein File ist OK — Beleg kann ohne Foto/PDF erfasst werden.
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-[8px] border border-[#1e2130] bg-[#13161f] p-3 text-[11px] text-[#4a5070]">
              Datei-Ersetzen oder Datei-Entfernen kommt in einem späteren
              Update. Aktuelle Datei (falls vorhanden) bleibt unverändert.
            </div>
          )}

          {/* Betrag + Währung */}
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div>
              <label className={labelClass}>Betrag</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className={inputClass}
              />
            </div>
            <div>
              {/* §6.6 Currency-Lock */}
              <label className={labelClass}>Währung</label>
              <div className="rounded-[8px] border border-[#1e2130] bg-[#13161f] px-3 py-2 text-[13px] font-semibold text-[#94a3b8]">
                CHF
              </div>
            </div>
          </div>

          {/* Kategorie */}
          <div>
            <label className={labelClass}>Kategorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={inputClass}
            >
              {categoryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {/* Datum */}
          <div>
            <label className={labelClass}>Beleg-Datum</label>
            <input
              type="date"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
              required
              className={inputClass}
            />
          </div>

          {/* Beschreibung */}
          <div>
            <label className={labelClass}>Beschreibung (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="z. B. Lieferant, Rechnungs-Nr., Zweck …"
              className={inputClass}
            />
          </div>

          {/* §6.7 Bezahlt von */}
          <div>
            <label className={labelClass}>Bezahlt von</label>
            <select
              value={paidByUserId}
              onChange={(e) => setPaidByUserId(e.target.value)}
              className={inputClass}
            >
              <option value="">Firma (direkt bezahlt)</option>
              {orgUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.email || u.id}
                </option>
              ))}
            </select>
          </div>

          {/* §6.8 Verknüpfungs-Selectoren (4× plain select) */}
          <details className="rounded-[8px] border border-[#1e2130] bg-[#0d0f14] p-3">
            <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-[0.09em] text-[#4a5070]">
              Verknüpfungen (optional)
            </summary>
            <div className="mt-3 grid gap-3">
              <div>
                <label className={labelClass}>Unit</label>
                <select
                  value={unitId}
                  onChange={(e) => setUnitId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">— keine —</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.short_unit_id || ""}
                      {u.short_unit_id ? " · " : ""}
                      {u.address || u.title || u.id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Mieter</label>
                <select
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">— keine —</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name || t.email || t.id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Mietverhältnis</label>
                <select
                  value={tenancyId}
                  onChange={(e) => {
                    setTenancyId(e.target.value);
                    setTenancyRevenueId("");
                  }}
                  className={inputClass}
                >
                  <option value="">— keines —</option>
                  {tenancies.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.tenant_name || t.tenant_id || ""}
                      {t.tenant_name && (t.unit_label || t.unit_short_unit_id)
                        ? " · "
                        : ""}
                      {t.unit_label || t.unit_short_unit_id || ""}
                      {!t.tenant_name && !t.unit_label && !t.unit_short_unit_id
                        ? t.id
                        : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* TenancyRevenue — nur bei rent_payment + tenancyId */}
              {type === "rent_payment" && tenancyId ? (
                <div>
                  <label className={labelClass}>Revenue-Zeile (Mietposition)</label>
                  <select
                    value={tenancyRevenueId}
                    onChange={(e) => setTenancyRevenueId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">— keine (Sonderzahlung) —</option>
                    {tenancyRevenueOptions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.type} · CHF {Number(r.amount_chf).toFixed(2)} /{" "}
                        {r.frequency}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-[#4a5070]">
                    Optional. Leer lassen für Sonderzahlung außerhalb des
                    Mietplans.
                  </p>
                </div>
              ) : null}
            </div>
          </details>

          <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
            {/* Delete-Trigger als sekundäre Aktion links (nur edit-Mode). */}
            {mode === "edit" ? (
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={submitting || deleting}
                className="rounded-[8px] border border-rose-500/30 bg-transparent px-3 py-2 text-[12px] font-medium text-rose-300 hover:bg-rose-500/10 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                🗑️ Beleg löschen
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onClose?.()}
                disabled={submitting || deleting}
                className="rounded-[8px] border border-[#1e2130] bg-transparent px-4 py-2 text-[13px] font-semibold text-[#94a3b8] hover:bg-[#13161f] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={submitting || deleting}
                className="rounded-[8px] bg-[#5b8cff] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#4a7ae8] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting
                  ? mode === "edit"
                    ? "Speichere …"
                    : "Erfasse …"
                  : submitLabel}
              </button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

      {/* SP6 — Soft-Delete-AlertDialog */}
      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={(next) => {
          if (!deleting) setDeleteConfirmOpen(next);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Beleg wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Dieser Beleg wird in den Papierkorb verschoben und ist
                danach nicht mehr in der Liste sichtbar.
              </span>
              <span className="block text-[12px] text-[#94a3b8]">
                Schweizer OR Art. 958f: Buchhaltungs-Belege müssen 10 Jahre
                aufbewahrt werden. Der Datensatz bleibt in der Datenbank
                erhalten und kann vom Support wiederhergestellt werden.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            >
              {deleting ? "Lösche …" : "Endgültig löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
